import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiPlay,
  FiBook,
  FiCode,
  FiAward,
  FiUsers,
  FiTrendingUp,
} from 'react-icons/fi';
import { HiOutlinePuzzle } from 'react-icons/hi';
import { FaLinkedin } from 'react-icons/fa';
import { TbEngine } from 'react-icons/tb';
import { Player } from '@lottiefiles/react-lottie-player';
import { MdOutlineHardware } from 'react-icons/md';
import walkingRobotAnimation from '../../assets/lottie/walking-robot.json';

import {
  Container,
  Header,
  HeaderLeft,
  Logo,
  Nav,
  NavItem,
  Hero,
  HeroContent,
  HeroTitle,
  HeroSubtitle,
  HeroButtons,
  Button,
  ButtonSecondary,
  HeroImage,
  WhiteCardSection,
  WhiteCardContent,
  TitleWrapper,
  WhiteCardTitle,
  BrowserWrapper,
  BrowserImage,
  FeaturesSection,
  FeaturesSectionTitle,
  FeaturesCardsGrid,
  FeatureCard,
  FeatureVideoBackground,
  FeatureCardContent,
  FeatureCardTitle,
  FeatureCardSubtitle,
  WhyUs,
  WhyUsTitle,
  FeaturesGrid,
  FeatureCardOld,
  FeatureIcon,
  FeatureTitle,
  FeatureDescription,
  CTA,
  CTAContent,
  CTATitle,
  CTASubtitle,
  Footer,
  FooterContent,
  FooterLinks,
  FooterLink,
  FooterSocial,
  SocialLink,
  FooterLogo,
} from './styles';

const Landing: React.FC = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const { t } = useTranslation();

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <Logo>
            <div style={{ display: 'flex' }}>
              <HiOutlinePuzzle size={28} />
            </div>
            <p>{t('LearnFirst')}</p>
          </Logo>
          <Nav>
            <NavItem onClick={() => scrollToSection('features')}>
              {t('Features')}
            </NavItem>
            <NavItem onClick={() => scrollToSection('experience')}>
              {t('Our Experience')}
            </NavItem>
            <NavItem onClick={() => scrollToSection('contact')}>
              {t('Contact')}
            </NavItem>
          </Nav>
        </HeaderLeft>
        <HeroButtons>
          <Link to="/signin">
            <ButtonSecondary>{t('Sign In')}</ButtonSecondary>
          </Link>
          <Link to="/signup">
            <Button>{t('Get Started')}</Button>
          </Link>
        </HeroButtons>
      </Header>

      <Hero>
        <HeroContent>
          <HeroTitle>
            {t('Master Robotics & Programming')}
            <br />
            <span>{t('with Interactive Learning')}</span>
          </HeroTitle>
          <HeroSubtitle>
            {t(
              'Learn robotics, programming, and engineering through hands-on projects, interactive simulations, and real-world applications. Perfect for students, educators, and robotics enthusiasts.',
            )}
          </HeroSubtitle>
          <HeroButtons>
            <Link to="/signup">
              <Button>{t('Start Learning')}</Button>
            </Link>
          </HeroButtons>
        </HeroContent>
      </Hero>

      <WhiteCardSection>
        <WhiteCardContent>
          <TitleWrapper>
            <MdOutlineHardware size={32} style={{ color: '#1d62d1' }} />
          </TitleWrapper>
          <WhiteCardTitle>
            {t('Hands-on Robotics,')}
            <br />
            {t('No Hardware Required')}
          </WhiteCardTitle>
          <BrowserWrapper>
            <BrowserImage>
              <img
                src="https://learnfirst-bucket.s3.us-east-2.amazonaws.com/Screenshot+(137).png"
                alt="Interactive Simulation Screenshot"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '5px',
                }}
              />
            </BrowserImage>
          </BrowserWrapper>
        </WhiteCardContent>
        <HeroImage>
          <Player
            autoplay
            loop
            src={walkingRobotAnimation}
            style={{ height: '350px', width: '400px' }}
          />
        </HeroImage>
      </WhiteCardSection>

      <FeaturesSection id="features">
        <FeaturesSectionTitle>
          {t('Discover Our Learning Features')}
        </FeaturesSectionTitle>
        <FeaturesCardsGrid>
          <FeatureCard>
            <FeatureVideoBackground>
              <video
                src="https://learnfirst-bucket.s3.us-east-2.amazonaws.com/ass.mp4"
                autoPlay
                loop
                muted
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </FeatureVideoBackground>
            <FeatureCardContent>
              <FeatureCardTitle>{t('Learning into Action')}</FeatureCardTitle>
              <FeatureCardSubtitle>
                {t(
                  'Complete hands-on assignments with real-time feedback and guidance',
                )}
              </FeatureCardSubtitle>
            </FeatureCardContent>
          </FeatureCard>

          <FeatureCard>
            <FeatureVideoBackground>
              <video
                src="https://learnfirst-bucket.s3.us-east-2.amazonaws.com/sld.mp4"
                autoPlay
                loop
                muted
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </FeatureVideoBackground>
            <FeatureCardContent>
              <FeatureCardTitle>{t('Virtual Robot Assembly')}</FeatureCardTitle>
              <FeatureCardSubtitle>
                {t(
                  'Explore and learn about different robot parts and their functions',
                )}
              </FeatureCardSubtitle>
            </FeatureCardContent>
          </FeatureCard>

          <FeatureCard>
            <FeatureVideoBackground>
              <video
                src="https://learnfirst-bucket.s3.us-east-2.amazonaws.com/acf.mp4"
                autoPlay
                loop
                muted
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </FeatureVideoBackground>
            <FeatureCardContent>
              <FeatureCardTitle>{t('Achievements')}</FeatureCardTitle>
              <FeatureCardSubtitle>
                {t(
                  'Track your progress and unlock achievements as you master robotics',
                )}
              </FeatureCardSubtitle>
            </FeatureCardContent>
          </FeatureCard>
        </FeaturesCardsGrid>
      </FeaturesSection>

      <WhyUs id="experience">
        <WhyUsTitle>{t('The LearnFirst Experience')}</WhyUsTitle>
        <FeaturesGrid>
          <FeatureCardOld>
            <FeatureIcon>
              <FiCode size={32} />
            </FeatureIcon>
            <FeatureTitle>{t('Interactive Programming')}</FeatureTitle>
            <FeatureDescription>
              {t(
                'Learn programming with real-time code execution, visual feedback, and step-by-step guidance through complex concepts.',
              )}
            </FeatureDescription>
          </FeatureCardOld>

          <FeatureCardOld>
            <FeatureIcon>
              <FiBook size={32} />
            </FeatureIcon>
            <FeatureTitle>{t('Comprehensive Curriculum')}</FeatureTitle>
            <FeatureDescription>
              {t(
                'From basic electronics to advanced robotics, our curriculum covers everything you need to become a robotics expert.',
              )}
            </FeatureDescription>
          </FeatureCardOld>

          <FeatureCardOld>
            <FeatureIcon>
              <FiTrendingUp size={32} />
            </FeatureIcon>
            <FeatureTitle>{t('Progress Tracking')}</FeatureTitle>
            <FeatureDescription>
              {t(
                'Monitor your learning journey with detailed progress reports, achievements, and personalized recommendations.',
              )}
            </FeatureDescription>
          </FeatureCardOld>
        </FeaturesGrid>
      </WhyUs>

      <CTA>
        <CTAContent>
          <CTATitle>{t('Ready to Start Your Robotics Journey?')}</CTATitle>
          <CTASubtitle>
            {t(
              'Join thousands of learners who are already building the future with robotics and programming.',
            )}
          </CTASubtitle>
          <Link to="/signup">
            <Button>{t('Get Started')}</Button>
          </Link>
        </CTAContent>
      </CTA>

      <Footer id="contact">
        <FooterContent>
          <FooterLogo>
            <Logo>
              <div style={{ display: 'flex' }}>
                <HiOutlinePuzzle size={22} />
              </div>
              <p>{t('LearnFirst')}</p>
            </Logo>
            <p>
              {t(
                'Empowering the next generation of robotics engineers and programmers.',
              )}
            </p>
          </FooterLogo>
          <FooterLinks>
            <div>
              <h4>{t('Get to know us')}</h4>
              <FooterLink href="#features">{t('Features')}</FooterLink>
              <FooterLink href="#experience">{t('Our Experience')}</FooterLink>
              {/* <FooterLink href="#pricing">Pricing</FooterLink> */}
            </div>
            <div>
              <h4>{t('Platform')}</h4>
              <FooterLink href="/library">{t('Library')}</FooterLink>
              <FooterLink href="/courses">{t('Courses')}</FooterLink>
              {/* <FooterLink href="#pricing">Pricing</FooterLink> */}
            </div>
            <div>
              <h4>{t('Support')}</h4>
              <FooterLink href="mailto:contact@learnfirst.app">
                {t('Contact')}
              </FooterLink>
              {/* <FooterLink href="#help">Help Center</FooterLink>
              <FooterLink href="#community">Community</FooterLink> */}
            </div>
            {/* <div>
              <h4>Company</h4>
              <FooterLink href="#about">About</FooterLink>
              <FooterLink href="#careers">Careers</FooterLink>
              <FooterLink href="#privacy">Privacy</FooterLink>
            </div> */}
          </FooterLinks>
        </FooterContent>
        <FooterSocial>
          <p>{`© 2025 ${t('LearnFirst')}. ${t('All rights reserved.')}`}</p>
          <div>
            <SocialLink
              href="https://www.linkedin.com/company/learnfirstrobotics"
              target="_blank"
            >
              <FaLinkedin size={28} />
            </SocialLink>
            {/* <SocialLink href="#twitter">Twitter</SocialLink>
            <SocialLink href="#github">GitHub</SocialLink> */}
          </div>
        </FooterSocial>
      </Footer>
    </Container>
  );
};

export default Landing;
